/**
 * Community user profile — GET /users/me, PUT /users/{cognitoSub}.
 * PUT body mirrors iOS UserAPI.APIUserPutBody (backend-aligned, not the richer UI model).
 */

import { API_BASE_URL, buildAuthHeaders, getUserSubFromToken } from './apiClient';

export interface UserPrivacySettings {
  shareTrips: boolean;
  shareLocation: boolean;
  allowDiscovery: boolean;
  showProfile: string;
}

export interface CommunityUserProfile {
  id: string;
  username: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  bio: string | null;
  homeCity: string | null;
  ageRange: string | null;
  contactEmail: string | null;
  mobileNumber: string | null;
  whatsappNumber: string | null;
  facebook: string | null;
  instagram: string | null;
  twitter: string | null;
  threads: string | null;
  linkedin: string | null;
  snapchat: string | null;
  tiktok: string | null;
  signal: string | null;
  discord: string | null;
  cruiseCount: number;
  nightsCount: number;
  connectionCount: number;
  privacySettings: UserPrivacySettings;
}

/** Fields accepted by PUT /users/{sub} (iOS APIUserPutBody). */
export interface UserProfilePutBody {
  email: string;
  firstName: string;
  lastName: string;
  shareTrips: boolean;
  shareLocation: boolean;
  allowDiscovery: boolean;
  showProfile: string;
  cruiseCount: number;
  nightsCount: number;
  connectionCount: number;
  travelStyleTags?: string[] | null;
  bio?: string | null;
  ageRange?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

function numOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function parseCommunityUserProfile(raw: unknown): CommunityUserProfile {
  const o = asRecord(raw);
  const nested = asRecord(o.privacySettings);
  const privacy: UserPrivacySettings = {
    shareTrips: Boolean(
      nested.shareTrips ?? o.shareTrips ?? false
    ),
    shareLocation: Boolean(
      nested.shareLocation ?? o.shareLocation ?? false
    ),
    allowDiscovery: Boolean(
      nested.allowDiscovery ?? o.allowDiscovery ?? false
    ),
    showProfile:
      strOrNull(nested.showProfile ?? o.showProfile) || 'CONNECTIONS_ONLY',
  };

  const email = strOrNull(o.email) || '';
  const username = strOrNull(o.username) || email;
  const firstName = strOrNull(o.firstName);
  const lastName = strOrNull(o.lastName);
  let displayName = strOrNull(o.displayName);
  if (!displayName) {
    const full = [firstName, lastName].filter(Boolean).join(' ').trim();
    displayName = full || null;
  }

  return {
    id: strOrNull(o.id) || '',
    username,
    email,
    firstName,
    lastName,
    displayName,
    bio: strOrNull(o.bio),
    homeCity: strOrNull(o.homeCity),
    ageRange: strOrNull(o.ageRange),
    contactEmail: strOrNull(o.contactEmail),
    mobileNumber: strOrNull(o.mobileNumber),
    whatsappNumber: strOrNull(o.whatsappNumber),
    facebook: strOrNull(o.facebook),
    instagram: strOrNull(o.instagram),
    twitter: strOrNull(o.twitter),
    threads: strOrNull(o.threads),
    linkedin: strOrNull(o.linkedin),
    snapchat: strOrNull(o.snapchat),
    tiktok: strOrNull(o.tiktok),
    signal: strOrNull(o.signal),
    discord: strOrNull(o.discord),
    cruiseCount: numOrZero(o.cruiseCount),
    nightsCount: numOrZero(o.nightsCount),
    connectionCount: numOrZero(o.connectionCount),
    privacySettings: privacy,
  };
}

export function buildUserProfilePutBody(
  profile: CommunityUserProfile,
  updates: {
    firstName: string;
    lastName: string;
    bio: string | null;
    ageRange: string | null;
    privacy: UserPrivacySettings;
  }
): UserProfilePutBody {
  return {
    email: profile.email,
    firstName: updates.firstName.trim(),
    lastName: updates.lastName.trim(),
    shareTrips: updates.privacy.shareTrips,
    shareLocation: updates.privacy.shareLocation,
    allowDiscovery: updates.privacy.allowDiscovery,
    showProfile: updates.privacy.showProfile || 'CONNECTIONS_ONLY',
    cruiseCount: profile.cruiseCount,
    nightsCount: profile.nightsCount,
    connectionCount: profile.connectionCount,
    travelStyleTags: null,
    bio: updates.bio,
    ageRange: updates.ageRange,
  };
}

export async function getCurrentUserProfile(): Promise<CommunityUserProfile> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    throw new Error(`getCurrentUserProfile failed: ${response.status}`);
  }
  return parseCommunityUserProfile(await response.json());
}

export async function putCurrentUserProfile(
  body: UserProfilePutBody
): Promise<CommunityUserProfile> {
  const sub = getUserSubFromToken();
  if (!sub) {
    throw new Error('putCurrentUserProfile: missing Cognito sub');
  }
  const headers = await buildAuthHeaders(true);
  const response = await fetch(
    `${API_BASE_URL}/users/${encodeURIComponent(sub)}`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`putCurrentUserProfile failed: ${response.status}`);
  }
  return parseCommunityUserProfile(await response.json());
}
